import { FC, memo, useEffect, useMemo, useState } from 'react'
import { Button, Tag, Form, Input, Radio, Checkbox, Row, Drawer, Switch, Popover, Col } from 'antd'
import { ReactComponent as ArrowLeftIcon } from '@/assets/icons/arrowLeft.svg'
import Shape2 from '@/assets/images/shape2.png'
import styles from './index.module.scss'
import Help from '@/assets/images/help.png'
import { isEqual } from 'lodash'
import { LanguageEnum, useTranslation } from '@/services/i18n'
import { useGetApiCoSigner, useGetSweepNetworkInfo } from '@/hooks/sweep'
import {
  SaveOrUpdateDto,
  SweepConfig,
  SweepNetwork,
  SweepWallet,
} from '@/services/apis/sweep/models'
import WalletDrawer from '../WalletDrawer/index'
import SelectNetwork from './SelectNetwork'
import DrawerFooter from '@/components/DrawerFooter'
import Modal from '@/components/Modal'
import { handleSweepI18nTip } from '@/utils/utils'
import sweep from '@/services/apis/sweep'
import emitter from '@/utils/event'
import { REFRESH_SWEEP_RULE_LIST } from '@/constants/event'
import { AutoSweepRuleDetailModel } from '@/services/apis/policy/model'
import NetworkLogo from '@/components/NetworkLogo'
import AccountTypeTag from '@/components/AccountTypeTag'
import { handleScientificNotation } from '@/utils/number'

interface FormValues {
  remainGas: string
  onceRefillGas: string
  sweepRate: string
  maxFeeLimit: string
  triggerAmountUsd: string
  auditUserList: string[]
  isSwitch?: boolean
  forbidAml: boolean
}

interface Props {
  defaultNetwork?: SweepNetwork
  onClose: () => void
  data?: AutoSweepRuleDetailModel
  baseConfig: SweepConfig
}

const SweepStrategy: FC<Props> = ({ defaultNetwork, onClose, data, baseConfig }) => {
  const isModify = !!data
  const [selectedNetwork, setSelectedNetwork] = useState<SweepNetwork>()
  const network = useMemo(
    () => selectedNetwork || defaultNetwork,
    [defaultNetwork, selectedNetwork]
  )
  const additionNetworkInfo = useGetSweepNetworkInfo(network?.networkKey)
  const apiCoSigners = useGetApiCoSigner()
  const { t } = useTranslation()
  const [isOpenWalletSelector, setOpenWalletSelector] = useState(false)
  const [isOpenNetworkSelector, setOpenNetworkSelector] = useState(false)
  const [onceRefillGasErrMsg, setOnceRefillGasErrMsg] = useState('')
  const [isGasStation, setIsGasStaion] = useState(false)
  const [form] = Form.useForm<FormValues>()
  const destWallet = Form.useWatch<SweepWallet>('destWallet', form)
  const gasWallet = Form.useWatch<SweepWallet>('gasWallet', form)
  const isSwitch = Form.useWatch<boolean>('isSwitch', form)

  const [isDisabled, setDisabled] = useState(true)

  const formValues = Form.useWatch([], form)

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(values => {
        // 判断是否进行了修改

        if (preData && isEqual(preData, values)) {
          setDisabled(true)
          return
        }
        setDisabled(false)
        setOnceRefillGasErrMsg('')
      })
      .catch((err: any) => {
        console.log('Form validate error:', err)
        if (err.errorFields.length > 0) {
          setDisabled(true)
          const onceRefillGasFieldErr = err.errorFields.find(
            (field: any) => field.name?.[0] === 'onceRefillGas'
          )
          if (onceRefillGasFieldErr) {
            setOnceRefillGasErrMsg(onceRefillGasFieldErr.errors[0])
          } else {
            setOnceRefillGasErrMsg('')
          }
          return
        }
        if (!preData) {
          setDisabled(false)
          setOnceRefillGasErrMsg('')
        }
      })
  }, [formValues])
  const [preData, setPreData] = useState<any>(null)
  const { i18n } = useTranslation()

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        auditUserList: data.auditUserList,
        sweepRate: String(data.sweepRate),
        maxFeeLimit: handleScientificNotation(data.maxFeeLimit),
        triggerAmountUsd: handleScientificNotation(data.triggerAmountUsd),
        remainGas:
          data.remainGas !== undefined ? handleScientificNotation(data.remainGas) : undefined,
        onceRefillGas:
          data.onceRefillGas !== undefined
            ? handleScientificNotation(data.onceRefillGas)
            : undefined,
        forbidAml: data.forbidAml,
      })
      form.setFieldValue(['isSwitch'], data.openGasStation)
      form.setFieldValue(['destWallet'], {
        accountName: data.destinationName,
        nativeAddress: data.destinationAddress,
        accountType: data.destinationType,
        blockchainTypeName: data.blockchainTypeName,
        id: data.destinationId,
        destType: data.destinationType,
      })
      if (data.openGasStation) {
        form.setFieldValue(['gasWallet'], {
          nativeAddress: data.gasStationAddress,
          blockchainTypeName: data.blockchainTypeName,
          accountName: data.gasStationAccountName,
          id: data.gasStationAccountKey,
          accountType: data.gasStationAccountType,
          nativeBalance: data.gasStationAccountBalance,
          nativeSymbol: data.nativeSymbol,
        })
      }
      setPreData(form.getFieldsValue(true))
    }
  }, [data])
  const [saveLoading, setSaveLoading] = useState(false)

  const handleCloseNetworkSelector = () => {
    setOpenNetworkSelector(false)
  }
  const handleSelectNetwork = (network: SweepNetwork) => {
    setSelectedNetwork(network)
    handleCloseNetworkSelector()
  }
  const gasValidator = (_: any, value: string) => {
    const maxFeeLimit = form.getFieldValue('maxFeeLimit')

    if (!value?.trim?.() && form.isFieldTouched('onceRefillGas')) {
      return Promise.reject(t('autoSweep.emptyGasStation'))
    }
    if (Number(value) <= 0) {
      return Promise.reject(t('autoSweep.onceFillGasGreater0'))
    }
    if (Number(value) < Number(maxFeeLimit)) {
      return Promise.reject(t('autoSweep.moreGas'))
    }
    return Promise.resolve()
  }

  const handleOk = (isDraft: boolean) => {
    form
      .validateFields()
      .then(values => {
        if (isDraft) {
          setSaveLoading(true)
          handleFinish(values, isDraft)
          return
        }
        Modal.confirm({
          title: t('autoSweep.confirmSubmit'),
          content: <div>{t('autoSweep.submitText')}</div>,
          closable: true,
          onOk: () => handleFinish(values, isDraft),
        })
      })
      .catch(err => {
        console.log('validate error:', err)
      })
  }

  const handleFinish = async (values: FormValues, isDraft: boolean) => {
    let dto: SaveOrUpdateDto = {
      networkKey: network?.networkKey!,
      destinationId: destWallet?.id!,
      destinationType: destWallet?.destType!,
      destinationAddress: destWallet?.nativeAddress!,
      maxFeeLimit: values.maxFeeLimit,
      triggerAmountUsd: values.triggerAmountUsd,
      sweepRate: values.sweepRate,
      auditUserList: values.auditUserList,
      openGasStation: !!values.isSwitch,
      remainGas: values.remainGas,
      gasStationAccountKey: gasWallet?.id,
      gasStationAddress: gasWallet?.nativeAddress,
      onceRefillGas: values.onceRefillGas,
      modifyKey: data?.modifyKey,
      forbidAml: values.forbidAml,
    }
    if (isDraft) {
      const res = await sweep.saveOrUpdate(dto)
      setSaveLoading(false)
      if (res.success) {
        emitter.emit(REFRESH_SWEEP_RULE_LIST)
        onClose()
      }
      return
    }
    const res = await sweep.saveOrUpdateSubmit(dto)
    setSaveLoading(false)
    if (res.success) {
      const modal = Modal.info({
        icon: null,
        footer: null,
        content: (
          <div className={styles.approvalBox}>
            <div className={styles.top}>{t('autoSweep.submitted')}</div>
            <div className={styles.content}>{t('autoSweep.completeSubmit')}</div>
            <div className={styles.footer}>
              <Button
                type="primary"
                style={{ width: '140px', height: '40px' }}
                onClick={() => modal.destroy()}
              >
                {t('common.complete')}
              </Button>
            </div>
          </div>
        ),
        width: 335,
      })
      emitter.emit(REFRESH_SWEEP_RULE_LIST)
      onClose()
    }
  }
  const [curWallet, setCurWallet] = useState<{
    address: string
    type: string
  } | null>(null)
  const handleWalletOpen = (isGasStaion: boolean) => {
    setIsGasStaion(isGasStaion)
    setOpenWalletSelector(true)
    // 设置选中的钱包
    if (isGasStaion && gasWallet) {
      setCurWallet({
        address: gasWallet?.nativeAddress,
        type: gasWallet.destType,
      })
    } else if (!isGasStaion && destWallet) {
      setCurWallet({
        address: destWallet?.nativeAddress,
        type: destWallet.destType,
      })
    }
  }
  const handleSelectWallet = (wallet: SweepWallet, isGas: boolean) => {
    form.setFieldValue(isGas ? 'gasWallet' : 'destWallet', wallet)
  }
  // 只能输入数字
  const handleValue = (name: any, e: any) => {
    let val = ''
    const maxLen = 18
    const decimals = additionNetworkInfo?.nativeCoinDecimal || 2
    let numberValue = e.target.value.replace(/[^\d.]/g, '')
    if (numberValue.includes('.')) {
      const [intergerPart, decimalPart] = numberValue.split('.')
      const intergerLen = intergerPart.length
      const decimalLen = maxLen - intergerLen > decimals ? decimals : maxLen - intergerLen
      const truncatedDecimal = decimalPart ? decimalPart.slice(0, decimalLen) : ''
      val = intergerPart + '.' + truncatedDecimal
    } else {
      val = numberValue.slice(0, maxLen)
    }
    form.setFieldValue([name], val)
  }
  return (
    <div className={styles.containerWrapper}>
      <div className={styles.sweepContainer}>
        <div className={styles.line}>
          <div className={styles.title}>{t('autoSweep.autoSweepNet')}</div>
          <Button
            className={styles.cardBtn}
            onClick={() => setOpenNetworkSelector(true)}
            disabled={isModify}
            style={{ height: '46px' }}
          >
            <div className={styles.cardText}>
              <div className={styles.network}>
                <NetworkLogo src={network?.networkLogo} />
                <div>
                  {`${network?.networkName}`}
                  <Tag>{`${network?.blockchainTypeName}`}</Tag>
                </div>
                <img src={Shape2} alt="" className={styles.right} />
              </div>
            </div>
          </Button>
        </div>
        <div className={styles.line}>
          <div className={styles.title}>{t('autoSweep.sweepWallet')}</div>
          <div
            className={styles.walletDesc}
            dangerouslySetInnerHTML={{
              __html: handleSweepI18nTip(t, baseConfig),
            }}
          />
        </div>
        <Form name="autoSweep" labelWrap={false} form={form} requiredMark={false}>
          <div className={styles.formTitle}>{t('autoSweep.walletAddress')}</div>
          <Form.Item
            name="destWallet"
            rules={[{ required: true, message: t('autoSweep.emptyAddress') as string }]}
          >
            <Button
              className={styles.cardBtn}
              onClick={() => {
                handleWalletOpen(false)
              }}
            >
              <div className={styles.cardBox}>
                {destWallet ? (
                  <div className={styles.left}>
                    <div className={styles.name}>
                      {destWallet.accountName}
                      <AccountTypeTag type={destWallet.accountType} />
                    </div>
                    <div className={styles.destAddress}>{destWallet.nativeAddress}</div>
                  </div>
                ) : (
                  <span className={styles.placeholder}>{t('autoSweep.setWalletAdd')}</span>
                )}
                <img src={Shape2} alt="" className={styles.right} />
              </div>
            </Button>
          </Form.Item>
          <div className={styles.formTitle}>{t('autoSweep.autoSweepCondition')}</div>
          <Form.Item label={t('autoSweep.valueCondition')}>
            <Form.Item
              name="triggerAmountUsd"
              rules={[
                {
                  validator: (_: any, value: string) => {
                    if (!value?.trim?.()) {
                      return Promise.reject(t('autoSweep.emptyCondition'))
                    }
                    if (Number(value) < 1) {
                      return Promise.reject(t('autoSweep.triggerAmountUsdGreater1'))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
              noStyle
            >
              <Input
                placeholder={t('autoSweep.amount') as string}
                suffix={<div>USD</div>}
                className={styles.countInput}
                onChange={e => handleValue('triggerAmountUsd', e)}
              />
            </Form.Item>
            <Popover
              content={<div style={{ width: '200px' }}>{t('autoSweep.popoverText.condition')}</div>}
            >
              <img alt="" src={Help} className={styles.help}></img>
            </Popover>
          </Form.Item>
          <div className={styles.formTitle}>{t('autoSweep.transactionConfiguration')}</div>
          <Form.Item label={t('autoSweep.collectionFrequency')}>
            <Form.Item name="sweepRate" rules={[{ required: true }]} noStyle>
              <Radio.Group size="large" className={styles.sweepRateGroup}>
                {['15', '30', '60', '120', '300'].map(val => (
                  <Radio.Button value={val} key={val}>
                    {val}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
            <span className={styles.time}>{t('autoSweep.time')}</span>
            <Popover
              content={
                <div style={{ width: '200px' }}>{t('autoSweep.popoverText.Configuration')}</div>
              }
            >
              <img
                alt=""
                src={Help}
                width={16}
                style={{ marginLeft: '10px', marginTop: '-2px' }}
              ></img>
            </Popover>
          </Form.Item>
          <Form.Item label={`${t('autoSweep.networkFee')} ≤`}>
            <Form.Item
              name="maxFeeLimit"
              noStyle
              rules={[
                {
                  validator: (_: any, value: string) => {
                    if (!value?.trim?.()) {
                      return Promise.reject(t('autoSweep.emptyFee'))
                    }
                    if (Number(value) <= 0) {
                      return Promise.reject(t('autoSweep.feeGreater0'))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Input
                suffix={<div>{additionNetworkInfo?.nativeSymbol}</div>}
                className={styles.countInput}
                onChange={e => handleValue('maxFeeLimit', e)}
              />
            </Form.Item>
            <Popover
              content={<div style={{ width: '200px' }}>{t('autoSweep.popoverText.fee')}</div>}
            >
              <img alt="" src={Help} className={styles.help} />
            </Popover>
          </Form.Item>
          {additionNetworkInfo?.supportGasStation && (
            <>
              <Form.Item label={`${t('autoSweep.gas')}`}>
                <Form.Item
                  name="remainGas"
                  rules={[
                    {
                      validator: (_: any, value: string) => {
                        if (!value?.trim?.()) {
                          return Promise.reject(t('autoSweep.emptyGas'))
                        }
                        if (network?.blockchainTypeName === 'NEAR' && Number(value) < 0.001) {
                          return Promise.reject(t('autoSweep.nearMinGas'))
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                  noStyle
                >
                  <Input
                    suffix={<div>{additionNetworkInfo?.nativeSymbol}</div>}
                    className={styles.countInput}
                    onChange={e => handleValue('remainGas', e)}
                  />
                </Form.Item>
                <Popover
                  content={<div style={{ width: '200px' }}>{t('autoSweep.popoverText.gas')}</div>}
                >
                  <img alt="" src={Help} className={styles.help} />
                </Popover>
              </Form.Item>
              <Form.Item label="Gas Station" className={styles.gasStationItem}>
                <Form.Item name="isSwitch" noStyle valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Popover
                  content={
                    <div style={{ width: '200px' }}>{t('autoSweep.popoverText.gasStation')}</div>
                  }
                >
                  <img alt="" src={Help} className={styles.help} />
                </Popover>
              </Form.Item>
            </>
          )}
          {isSwitch && (
            <>
              <Form.Item
                name="gasWallet"
                rules={[{ required: true, message: t('autoSweep.emptyGasStation') as string }]}
                style={{ marginTop: '-20px', marginBottom: '10px' }}
              >
                <Button
                  className={styles.cardBtn}
                  onClick={() => {
                    handleWalletOpen(true)
                  }}
                >
                  <div className={styles.cardBox}>
                    {gasWallet ? (
                      <div className={styles.left}>
                        <div className={styles.name}>
                          {gasWallet.accountName}
                          <AccountTypeTag type={gasWallet.accountType} />
                        </div>
                        <div className={styles.nativeAddress}>{gasWallet.nativeAddress}</div>
                        <div className={styles.nativeAddress}>
                          {t('common.balance')}：{gasWallet.nativeBalance} {gasWallet.nativeSymbol}
                        </div>
                      </div>
                    ) : (
                      <span className={styles.placeholder}>{t('autoSweep.selectGasStation')}</span>
                    )}
                    <img src={Shape2} className={styles.right} alt="" />
                  </div>
                </Button>
              </Form.Item>
              <Form.Item
                label={`${t('autoSweep.supplementGas')}`}
                name="onceRefillGas"
                rules={[{ validator: gasValidator }]}
                validateStatus="success"
                help={onceRefillGasErrMsg}
                className={styles.onceRefillGasFormItem}
              >
                <Input
                  suffix={<div>{additionNetworkInfo?.nativeSymbol}</div>}
                  className={styles.countInput}
                  onChange={e => handleValue('onceRefillGas', e)}
                />
              </Form.Item>
            </>
          )}
          <div>{t('autoSweep.transactionApproval')}</div>
          <div className={styles.apiCoSigner}>
            {t('autoSweep.apiCoSigner')}
            <a href={baseConfig.apiCoSignerUrl} target="_blank" rel="noreferrer">
              {t('autoSweep.findApi')}
            </a>
          </div>
          <Form.Item
            name="auditUserList"
            rules={[{ required: true, message: t('autoSweep.emptySinger') as string }]}
          >
            <Checkbox.Group style={{ width: '100%' }}>
              <Row>
                {apiCoSigners.map(item => {
                  return (
                    <Col span={24} style={{ marginBottom: '15px' }} key={item?.userKey}>
                      <Checkbox value={item?.userKey}>{item.apiName}</Checkbox>
                    </Col>
                  )
                })}
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label={`${t('autoSweep.aml')}`} className={styles.gasStationItem}>
            <Form.Item name="forbidAml" noStyle valuePropName="checked">
              <Switch defaultChecked />
            </Form.Item>
            <Popover
              content={<div style={{ width: '300px' }}>{t('autoSweep.popoverText.amlTip')}</div>}
            >
              <img alt="" src={Help} className={styles.help} />
            </Popover>
          </Form.Item>
        </Form>
      </div>
      <DrawerFooter>
        <Button className={styles.btn} onClick={onClose}>
          {t('common.cancel')}
        </Button>
        {data?.activeStatus !== 1 && (
          <Button
            className={styles.btn}
            type="primary"
            disabled={isDisabled}
            onClick={() => handleOk(true)}
            loading={saveLoading}
          >
            {t('autoSweep.saveDraft')}
          </Button>
        )}
        <Button
          className={styles.btn}
          type="primary"
          disabled={isDisabled}
          onClick={() => handleOk(false)}
          style={i18n.language === LanguageEnum.EN_US ? { width: '220px' } : { width: '180px' }}
        >
          {t('autoSweep.saveAndSubmission')}
        </Button>
      </DrawerFooter>
      <Drawer
        open={isOpenNetworkSelector}
        width={560}
        title={t('autoSweep.selectNetTitle')}
        footer={null}
        closeIcon={<ArrowLeftIcon onClick={handleCloseNetworkSelector} />}
        mask={false}
      >
        <SelectNetwork
          okText={t('common.confirm')}
          defaultValue={network!}
          onOk={handleSelectNetwork}
          onCancel={handleCloseNetworkSelector}
        />
      </Drawer>
      <WalletDrawer
        open={isOpenWalletSelector}
        onClose={() => {
          setOpenWalletSelector(false)
        }}
        isGasStation={isGasStation}
        selectWallet={handleSelectWallet}
        coinKey={additionNetworkInfo?.nativeCoinKey}
        blockchainTypeKey={additionNetworkInfo?.blockchainTypeKey}
        curWallet={curWallet}
      />
    </div>
  )
}

export default memo(SweepStrategy)
